import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import * as pickerService from '../services/api/picker';
import * as ordersService from '../services/api/orders';
import * as usersService from '../services/api/users';
import { transformOrder } from '../services/transformers/orderTransformer';
import { transformUser } from '../services/transformers/userTransformer';
import './PickupSummary.css';

const PickupSummary = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { currentPickup, pickupItems, calculateTotal, completePickup, plasticRates, loadPickups, setCurrentPickup } = useApp();
  const [totalAmount, setTotalAmount] = useState(0);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderProducts, setOrderProducts] = useState([]);
  const [productRates, setProductRates] = useState({});
  const [productNames, setProductNames] = useState({}); // Map of product_id to product_name
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [code, setCode] = useState(['', '', '', '']);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [otpError, setOtpError] = useState('');
  const [isGeneratingOtp, setIsGeneratingOtp] = useState(false);

  // Fetch fresh order data from /api/orders/{orderId} when summary page loads
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!orderId) return;

      // Clear previous order data to prevent showing wrong order
      setOrderProducts([]);
      setCustomerData(null);
      setTotalAmount(0);

      try {
        // Always fetch from /api/orders/{orderId} to get final details
        const orderResult = await ordersService.getOrderById(orderId);
        
        if (orderResult.success && orderResult.data) {
          const orderData = orderResult.data;
          
          // Verify we got the correct order
          const fetchedOrderId = orderData.order_id || orderData.id;
          if (fetchedOrderId && fetchedOrderId.toString() !== orderId.toString()) {
            console.error('Order ID mismatch! Expected:', orderId, 'Got:', fetchedOrderId);
            return;
          }
          
          // Transform order data for compatibility
          const transformedOrder = transformOrder(orderData);
          if (transformedOrder) {
            setCurrentPickup(transformedOrder);
          }
          
          // Get user_id from top level of order data (not from picker.user_id)
          const userId = orderData.user_id;
          
          // Fetch customer/user data using user_id
          if (userId) {
            try {
              const userResult = await usersService.getUserById(userId);
              if (userResult.success && userResult.data) {
                const transformedUser = transformUser(userResult.data);
                setCustomerData(transformedUser);
              }
            } catch (error) {
              console.error('Error fetching customer data:', error);
            }
          }
          
          // Get products from API response (API returns 'products' array)
          // Show ALL products in the order - no filtering, show everything
          const products = orderData.products || orderData.order_products || [];
          
          // Set all products without any filtering
          setOrderProducts(products);
          
          // Fetch product rates and names for base price calculation and display
          if (Object.keys(productRates).length === 0 || Object.keys(productNames).length === 0) {
            try {
              const productsResult = await pickerService.getProducts();
              if (productsResult.success && productsResult.data && Array.isArray(productsResult.data)) {
                const rates = {};
                const names = {};
                productsResult.data.forEach(product => {
                  if (product.product_id) {
                    const productId = product.product_id.toString();
                    rates[productId] = parseFloat(product.price || 0);
                    names[productId] = product.product_name || product.name || `Product ${productId}`;
                  }
                });
                setProductRates(rates);
                setProductNames(names);
              }
            } catch (error) {
              console.error('Error fetching products:', error);
            }
          }
        } else {
          console.error('Failed to fetch order:', orderResult.message);
        }
      } catch (error) {
        console.error('Error fetching order data:', error);
      }
    };

    fetchOrderData();
  }, [orderId, setCurrentPickup]);

  // Calculate total from actual_price of order_products and additional items
  useEffect(() => {
    let total = 0;
    
    // Add totals from order products
    if (orderProducts.length > 0) {
      total += orderProducts.reduce((sum, product) => {
        const actualPrice = parseFloat(product.actual_price) || 0;
        return sum + actualPrice;
      }, 0);
    }
    
    // Add totals from additional items using product rates (only items not already in orderProducts)
    const additionalItems = pickupItems.filter(item => {
      if (item.isFromOrder || !item.weight || item.weight <= 0) return false;
      
      // If item has an orderProductId, it means it was already added to order
      if (item.orderProductId) return false;
      
      // Check if there's a matching order product
      const productId = item.productId?.toString() || item.product_id?.toString();
      const itemWeight = parseFloat(item.weight) || 0;
      const existsInOrder = orderProducts.some(op => {
        const opProductId = op.product_id?.toString();
        const opQuantity = parseFloat(op.actual_quantity) || 0;
        return opProductId === productId && Math.abs(opQuantity - itemWeight) < 0.1;
      });
      
      return !existsInOrder;
    });
    
    if (additionalItems.length > 0) {
      const additionalTotal = additionalItems.reduce((sum, item) => {
        const productId = item.productId?.toString() || item.product_id?.toString();
        const basePrice = productId && productRates[productId] ? productRates[productId] : 0;
        const actualQuantity = parseFloat(item.weight) || 0;
        // Use customer_rate (rate) if available, otherwise use base price
        const rateToUse = item.rate != null && !isNaN(item.rate) && item.rate > 0 
          ? parseFloat(item.rate) 
          : basePrice;
        return sum + (rateToUse * actualQuantity);
      }, 0);
      total += additionalTotal;
    }
    
    // Fallback to old calculation if no order products and no product rates
    if (total === 0 && pickupItems.length > 0 && Object.keys(productRates).length === 0) {
      total = calculateTotal();
    }
    
    setTotalAmount(total);
  }, [orderProducts, pickupItems, productRates, calculateTotal]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showCodeModal || showDeclineModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCodeModal, showDeclineModal]);

  const handleConfirmAndGenerateCode = async () => {
    setIsGeneratingOtp(true);
    setOtpError('');
    
    try {
      const result = await pickerService.generateOtp(orderId);
      
      if (result.success) {
        setShowCodeModal(true);
        setCode(['', '', '', '']);
      } else {
        setOtpError(result.message || 'Failed to generate OTP. Please try again.');
        // Still show the modal so user can try again
        setShowCodeModal(true);
        setCode(['', '', '', '']);
      }
    } catch (error) {
      console.error('Error generating OTP:', error);
      setOtpError('Failed to generate OTP. Please try again.');
      // Still show the modal so user can try again
      setShowCodeModal(true);
      setCode(['', '', '', '']);
    } finally {
      setIsGeneratingOtp(false);
    }
  };

  const handleCodeInputChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 4).split('');
      const newCode = [...code];
      pastedCode.forEach((digit, i) => {
        if (index + i < 4) {
          newCode[index + i] = digit;
        }
      });
      setCode(newCode);
      // Focus next empty input or submit button
      const nextIndex = Math.min(index + pastedCode.length, 3);
      if (nextIndex < 3) {
        document.getElementById(`code-input-${nextIndex}`)?.focus();
      }
    } else {
      // Handle single digit input
      const newCode = [...code];
      newCode[index] = value.replace(/[^0-9]/g, '');
      setCode(newCode);
      
      // Auto-focus next input
      if (value && index < 3) {
        document.getElementById(`code-input-${index + 1}`)?.focus();
      }
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`code-input-${index - 1}`)?.focus();
    }
  };

  const handleCodeSubmit = async () => {
    const enteredCode = code.join('');
    
    if (enteredCode.length !== 4) {
      setOtpError('Please enter a 4-digit code');
      return;
    }

    setIsProcessing(true);
    setOtpError('');
    
    try {
      // Verify OTP first
      const verifyResult = await pickerService.verifyOtp(orderId, enteredCode);
      
      if (verifyResult.success) {
        // OTP verified successfully, update order status to PAID
        const result = await ordersService.updateOrder(orderId, { order_status: 'PAID' });
        
        if (result.success) {
          setShowCodeModal(false);
          setShowSuccessToast(true);
          // Reload pickups to get updated status
          await loadPickups();
          
          // Auto-hide toast after 3 seconds and navigate
          setTimeout(() => {
            setShowSuccessToast(false);
            navigate('/dashboard');
          }, 3000);
        } else {
          setOtpError(result.message || 'Failed to update order. Please try again.');
          setIsProcessing(false);
        }
      } else {
        // OTP verification failed
        setOtpError(verifyResult.message || 'Invalid OTP. Please try again.');
        setCode(['', '', '', '']); // Clear the code inputs
        setIsProcessing(false);
        // Focus first input after a short delay
        setTimeout(() => {
          document.getElementById('code-input-0')?.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setOtpError('Failed to verify OTP. Please try again.');
      setCode(['', '', '', '']); // Clear the code inputs
      setIsProcessing(false);
      // Focus first input after a short delay
      setTimeout(() => {
        document.getElementById('code-input-0')?.focus();
      }, 100);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining');
      return;
    }

    setIsProcessing(true);
    try {
      // Update summary field with decline reason (no order_status change)
      const result = await ordersService.updateOrder(orderId, { 
        summary: declineReason.trim() 
      });
      
      if (result.success) {
        // Reload pickups to get updated status
        await loadPickups();
        setShowDeclineModal(false);
        navigate('/dashboard');
      } else {
        alert(result.message || 'Failed to decline order. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error declining order:', error);
      alert('Failed to decline order. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!currentPickup) {
    return null;
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'PET': '💧',
      'HDPE': '🧪',
      'LDPE': '🛍',
      'PP': '🔵',
      'PS': '📦',
      'Other': '⋯'
    };
    return icons[category] || '📦';
  };

  // Get product name from order product
  const getProductName = (product) => {
    // First try to get from productNames map (from products API)
    const productId = product.product_id?.toString();
    if (productId && productNames[productId]) {
      return productNames[productId];
    }
    
    // Fallback to product object
    if (product.product && product.product.product_name) {
      return product.product.product_name;
    }
    if (product.product_name) {
      return product.product_name;
    }
    
    // Last fallback
    return product.category || `Product ${productId || 'Unknown'}`;
  };

  // Get price per kg
  const getPricePerKg = (product) => {
    const actualQty = parseFloat(product.actual_quantity) || 0;
    const actualPrice = parseFloat(product.actual_price) || 0;
    if (actualQty > 0 && actualPrice > 0) {
      return actualPrice / actualQty;
    }
    // Fallback to product price or rate
    if (product.product_id && productRates[product.product_id]) {
      return productRates[product.product_id];
    }
    if (product.product && product.product.price) {
      return parseFloat(product.product.price);
    }
    if (product.rate) {
      return parseFloat(product.rate);
    }
    return 0;
  };

  return (
    <div className="pickup-summary-screen">
      <div className="pickup-summary-container">
        <div className="header">
          <button className="header-back" onClick={() => {
            // Navigate back appropriately based on order status
            if (currentPickup?.status === 'PAID') {
              navigate('/dashboard');
            } else {
              navigate(`/pickup/${orderId}/process`);
            }
          }}>
            ←
          </button>
          <h1 className="header-title">Pickup Summary</h1>
          {currentPickup?.status === 'PAID' && (
            <div className="paid-header-badge">PAID</div>
          )}
        </div>

        <div className="total-payable-card">
          <div className="total-label">TOTAL PAYABLE</div>
          <div className="total-amount">₹{totalAmount.toFixed(2)}</div>
        </div>

        {(customerData || currentPickup) && (
          <div className="customer-card">
            <div className="customer-avatar-large">
              {customerData?.photo ? (
                <img src={customerData.photo} alt={customerData.name || 'Customer'} className="customer-avatar-image" />
              ) : (
                <span>{customerData?.name ? customerData.name.charAt(0).toUpperCase() : (currentPickup?.customer?.name ? currentPickup.customer.name.charAt(0).toUpperCase() : '👤')}</span>
              )}
            </div>
            <div className="customer-info-large">
              <div className="customer-name-large">{customerData?.name || currentPickup?.customer?.name || 'Customer'}</div>
              <div className="customer-phone">{customerData?.phone || customerData?.phone_number || currentPickup?.customer?.phone || currentPickup?.customer?.phone_number || 'No phone number'}</div>
            </div>
          </div>
        )}

        <div className="breakdown-section">
          <div className="breakdown-header">
            <h3 className="breakdown-title">Product Details</h3>
            <div className="items-badge">
              {orderProducts.length > 0 ? orderProducts.length : pickupItems.filter(item => item.weight != null && item.weight > 0).length} Items
            </div>
          </div>

          {/* All Products Section - Show ALL products from the order */}
          {orderProducts.length > 0 && (
            <div className="items-group">
              <div className="group-title">ORDER ITEMS</div>
              <div className="breakdown-list">
                {orderProducts.map((product, index) => {
                  const productName = getProductName(product);
                  const userQty = parseFloat(product.quantity) || 0;
                  const actualQty = parseFloat(product.actual_quantity) || 0;
                  const actualPrice = parseFloat(product.actual_price) || 0;
                  const pricePerKg = getPricePerKg(product);
                  const category = product.product?.product_name || product.category || 'Other';
                  
                  const basePrice = productRates[product.product_id] || pricePerKg;
                  
                  // Get customer_rate (negotiated rate) from API
                  const customerRate = product.customer_rate != null ? parseFloat(product.customer_rate) : null;
                  
                  return (
                    <div key={product.id || product.order_product_id || index} className="breakdown-item order-item">
                      <div className="item-image-circle">
                        {product.image ? (
                          <img src={product.image} alt={productName} className="item-image" />
                        ) : (
                          <span className="item-icon">{getCategoryIcon(category)}</span>
                        )}
                      </div>
                      <div className="item-info">
                        <div className="item-name">{productName}</div>
                        <div className="item-details">
                          <div className="quantity-row">
                            {userQty > 0 && (
                              <span className="user-qty">Expected Quantity: {userQty.toFixed(2)} kg</span>
                            )}
                            {actualQty > 0 ? (
                              <span className="actual-qty">Actual Quantity: {actualQty.toFixed(2)} kg</span>
                            ) : (
                              <span className="actual-qty pending">Pending confirmation</span>
                            )}
                          </div>
                          {basePrice > 0 && (
                            <div className="base-price-info">Base Price: ₹{basePrice.toFixed(2)}/kg</div>
                          )}
                          {customerRate != null && !isNaN(customerRate) && customerRate > 0 && (
                            <div className="base-price-info" style={{ color: '#10b981', fontWeight: '500' }}>
                              Negotiated Rate: ₹{customerRate.toFixed(2)}/kg
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="item-total">₹{actualPrice > 0 ? actualPrice.toFixed(2) : '0.00'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Additional Items Section */}
          {(() => {
            // Filter out items that have already been added to order (exist in orderProducts)
            const additionalItems = pickupItems.filter(item => {
              if (item.isFromOrder || !item.weight || item.weight <= 0) return false;
              
              // Check if this item already exists in orderProducts (was added via API)
              const productId = item.productId?.toString() || item.product_id?.toString();
              const itemWeight = parseFloat(item.weight) || 0;
              
              // If item has an orderProductId, it means it was already added to order
              if (item.orderProductId) return false;
              
              // Check if there's a matching order product with same product_id and similar quantity
              const existsInOrder = orderProducts.some(op => {
                const opProductId = op.product_id?.toString();
                const opQuantity = parseFloat(op.actual_quantity) || 0;
                return opProductId === productId && Math.abs(opQuantity - itemWeight) < 0.1;
              });
              
              return !existsInOrder;
            });
            
            return additionalItems.length > 0 && (
              <div className="items-group">
                <div className="group-title">ADDITIONAL ITEMS</div>
                <div className="breakdown-list">
                  {additionalItems.map((item, index) => {
                    // Get base price from productRates using productId
                    const productId = item.productId?.toString() || item.product_id?.toString();
                    const basePrice = productId && productRates[productId] ? productRates[productId] : 0;
                    const actualQuantity = parseFloat(item.weight) || 0;
                    // Use customer_rate (rate) if available, otherwise use base price
                    const rateToUse = item.rate != null && !isNaN(item.rate) && item.rate > 0 
                      ? parseFloat(item.rate) 
                      : basePrice;
                    // Calculate total using the rate (customer_rate if available, else base price)
                    const itemTotal = rateToUse * actualQuantity;
                    
                    const itemImage = item.photo || item.image || item.imageUrl;
                    const productName = item.category || 'Product';
                    
                    return (
                      <div key={item.id || index} className="breakdown-item">
                        <div className="item-image-circle">
                          {itemImage ? (
                            <img src={itemImage} alt={productName} className="item-image" />
                          ) : (
                            <span className="item-icon">{getCategoryIcon(item.category)}</span>
                          )}
                        </div>
                        <div className="item-info">
                          <div className="item-name">{productName}</div>
                          <div className="item-details">
                            <div className="quantity-row">
                              <span className="actual-qty">Actual Quantity: {actualQuantity.toFixed(2)} kg</span>
                            </div>
                            <div className="base-price-info">Base Price: ₹{basePrice.toFixed(2)}/kg</div>
                            {item.rate != null && !isNaN(item.rate) && item.rate > 0 && item.rate !== basePrice && (
                              <div className="base-price-info" style={{ color: '#10b981', fontWeight: '500' }}>
                                Negotiated Rate: ₹{item.rate.toFixed(2)}/kg
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="item-total">₹{itemTotal.toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {currentPickup?.status !== 'PAID' && (
          <div className="action-buttons">
            <button 
              className="btn-danger decline-btn" 
              onClick={() => setShowDeclineModal(true)}
              disabled={isProcessing}
            >
              Decline
            </button>
            <button 
              className="btn-primary agree-btn" 
              onClick={handleConfirmAndGenerateCode}
              disabled={isProcessing || isGeneratingOtp}
            >
              {isGeneratingOtp ? 'Generating Code...' : 'Confirm & Generate Code →'}
            </button>
          </div>
        )}

        {currentPickup?.status === 'PAID' && (
          <div className="paid-view-footer">
            <div className="paid-footer-message">
              <span className="paid-footer-icon">✓</span>
              <span>This order has been completed and paid</span>
            </div>
          </div>
        )}
      </div>

      {showDeclineModal && (
        <div className="modal-overlay" onClick={() => setShowDeclineModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Reason for Decline</h3>
            <textarea
              className="decline-reason-input"
              placeholder="Please provide a reason for declining this pickup..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows="4"
            />
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowDeclineModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleDecline}
                disabled={!declineReason.trim() || isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCodeModal && (
        <div className="modal-overlay" onClick={() => !isProcessing && !isGeneratingOtp && setShowCodeModal(false)}>
          <div className="modal-content code-modal" onClick={(e) => e.stopPropagation()}>
            <div className="code-modal-header">
              <h3 className="modal-title">Enter Verification Code</h3>
              <button
                className="code-modal-close"
                onClick={() => !isProcessing && !isGeneratingOtp && setShowCodeModal(false)}
                disabled={isProcessing || isGeneratingOtp}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="code-modal-body">
              <div className="code-icon-wrapper">
                <div className="code-icon">🔐</div>
              </div>
              <p className="code-instruction">Please enter the 4-digit verification code sent to the customer</p>
              {otpError && (
                <div className="otp-error-message">
                  <span className="otp-error-icon">⚠️</span>
                  <span>{otpError}</span>
                </div>
              )}
              <div className="code-input-container">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className={`code-input ${otpError ? 'code-input-error' : ''}`}
                    value={digit}
                    onChange={(e) => {
                      handleCodeInputChange(index, e.target.value);
                      setOtpError(''); // Clear error when user starts typing
                    }}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    disabled={isProcessing || isGeneratingOtp}
                    autoFocus={index === 0 && !otpError}
                  />
                ))}
              </div>
              <div className="code-hint">
                <span className="code-hint-icon">💡</span>
                <span>Check the customer's phone for the verification code</span>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowCodeModal(false);
                  setOtpError('');
                  setCode(['', '', '', '']);
                }}
                disabled={isProcessing || isGeneratingOtp}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleCodeSubmit}
                disabled={code.join('').length !== 4 || isProcessing || isGeneratingOtp}
              >
                {isProcessing ? (
                  <>
                    <span className="btn-spinner"></span>
                    Verifying...
                  </>
                ) : (
                  'Verify Code'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessToast && (
        <div className="success-toast-overlay">
          <div className="success-toast">
            <div className="success-toast-icon">✓</div>
            <div className="success-toast-content">
              <h3 className="success-toast-title">Payment Confirmed!</h3>
              <p className="success-toast-message">Order #{orderId} has been marked as PAID successfully.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PickupSummary;

